import { useMemo } from 'react';

import { AVERAGE_HOURS_IN_MONTH } from '@cast/constants';
import { useWorkloadsOptimizationSummaryQuery } from '@cast/features-cluster-shared';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { WorkloadOptimizationSummaryResponse } from '@cast/types';
import { nonNegativeGuard, toBig } from '@cast/utils';

export type InventoryWorkloadsAutoscalerBannerData = {
  percentage: number;
  optimizedCount: number;
  totalCount: number;
  reclaimedCpuCores: number;
  reclaimedMemoryGibs: number;
  reclaimedMonthlySavings: number;
  potentialCpuCores: number;
  potentialMemoryGibs: number;
  potentialMonthlySavings: number;
};

export const normalizeInventoryWorkloadsAutoscalerBannerData = (
  data: WorkloadOptimizationSummaryResponse
): InventoryWorkloadsAutoscalerBannerData => {
  const {
    totalCount,
    optimizedCount,
    originalRequestedCpuCores,
    requestedCpuCores,
    recommendedCpuCores,
    originalRequestedMemoryGibs,
    requestedMemory,
    recommendedMemory,
    costsPerHour,
  } = data;

  const originalRequested = costsPerHour?.originalRequested ?? 0;
  const requested = costsPerHour?.requested ?? 0;
  const recommended = costsPerHour?.recommended ?? 0;

  return {
    percentage: totalCount > 0 ? (optimizedCount / totalCount) * 100 : 0,
    optimizedCount,
    totalCount,
    reclaimedCpuCores: nonNegativeGuard(
      toBig(originalRequestedCpuCores).sub(requestedCpuCores).toNumber()
    ),
    reclaimedMemoryGibs: nonNegativeGuard(
      toBig(originalRequestedMemoryGibs).sub(requestedMemory).toNumber()
    ),
    reclaimedMonthlySavings: nonNegativeGuard(
      toBig(originalRequested)
        .sub(requested)
        .mul(AVERAGE_HOURS_IN_MONTH)
        .toNumber()
    ),
    // Remaining opportunity (requested → recommended), not API difference fields
    // which are recommended - requested in fixtures / OpenAPI wording.
    potentialCpuCores: nonNegativeGuard(
      toBig(requestedCpuCores).sub(recommendedCpuCores).toNumber()
    ),
    potentialMemoryGibs: nonNegativeGuard(
      toBig(requestedMemory).sub(recommendedMemory).toNumber()
    ),
    potentialMonthlySavings: nonNegativeGuard(
      toBig(requested).sub(recommended).mul(AVERAGE_HOURS_IN_MONTH).toNumber()
    ),
  };
};

export const useInventoryWorkloadsAutoscalerBannerData = () => {
  const { cluster } = useCluster();
  const { data, isLoading, isFetching, error, refetch } =
    useWorkloadsOptimizationSummaryQuery({
      clusterId: cluster.id,
      enabled: !!cluster.id,
    });

  const bannerData = useMemo(() => {
    if (!data) {
      return undefined;
    }
    return normalizeInventoryWorkloadsAutoscalerBannerData(data);
  }, [data]);

  return {
    data: bannerData,
    isLoading,
    isFetching,
    hasError: !!error,
    refetch,
  };
};
